import { FC, useEffect, useState } from 'react';
import { addDays } from 'date-fns';
import { uk } from 'date-fns/locale';
import { DateRange } from 'react-day-picker';

import { useIsMobile } from '@/hooks/use-mobile';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

import { useApiManagerStore } from '@/stores/apiManagerStore';

export const ReportAnalyticsPage: FC = () => {
  const { reportAnalytics, getReportAnalytics } = useApiManagerStore();
  const isMobile = useIsMobile();
  const [windowWidth, setWindowWidth] = useState<number>(
    typeof window !== 'undefined' ? window.innerWidth : 0,
  );

  useEffect(() => {
    getReportAnalytics();

    // Add window resize listener to track viewport width
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Dynamically determine number of months based on available width
  const getNumberOfMonths = () => {
    if (isMobile || windowWidth < 1200) {
      return 1;
    }
    return 2;
  };

  const handleEffectivenessReport = () => {
    console.log('Generating effectiveness report');
  };

  const handleVinReport = () => {
    console.log('Generating VIN report');
  };
  const [date, setDate] = useState<DateRange | undefined>({
    from: new Date(2022, 0, 20),
    to: addDays(new Date(2022, 0, 20), 20),
  });

  return (
    <div className="flex overflow-hidden px-3 py-5 sm:h-auto sm:max-w-[calc(100vw-330px)] sm:flex-1 sm:p-[50px]">
      <div className="flex flex-wrap gap-2 sm:gap-10">
        {/* Effectiveness Report Card */}
        <Card className="flex w-full flex-col justify-between shadow-sm sm:h-[330px] sm:w-[300px]">
          <CardHeader className="space-y-1 pb-2">
            <CardTitle className="text-xl font-medium leading-6 text-primary">
              {reportAnalytics.effectivenessReport.title}
            </CardTitle>
            <CardDescription className="text-xs font-medium text-primary">
              {reportAnalytics.effectivenessReport.description}
            </CardDescription>
          </CardHeader>
          <CardContent className="relative flex flex-col items-end justify-between sm:h-full">
            <div className="mb-5 space-y-2.5">
              <p className="text-xs font-medium text-primary">
                <span className="mr-1">&#9679;</span>
                {reportAnalytics.effectivenessReport.metrics.lateUploadedFiles}
              </p>
              <p className="text-xs font-medium text-primary">
                <span className="mr-1">&#9679;</span>
                {
                  reportAnalytics.effectivenessReport.metrics
                    .failedValidationFiles
                }
              </p>
            </div>
            <Popover>
              <PopoverTrigger asChild>
                <Button className="w-full" variant="outline">
                  Обрати дати
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className="flex w-auto flex-col gap-2 p-2"
                align="center"
                side={isMobile ? 'bottom' : 'right'}
                sideOffset={10}
                avoidCollisions={true}
                collisionPadding={20}
                style={{ maxHeight: '90vh', overflowY: 'auto' }}
              >
                <Calendar
                  initialFocus
                  mode="range"
                  defaultMonth={date?.from}
                  selected={date}
                  onSelect={setDate}
                  numberOfMonths={getNumberOfMonths()}
                  locale={uk}
                  className="max-w-full"
                  classNames={{
                    months:
                      'space-y-4 sm:space-x-4 sm:space-y-0 flex flex-col sm:flex-row',
                    month: 'space-y-4 w-full',
                  }}
                />
                <div className="sticky bottom-0 flex w-full justify-end bg-background p-2">
                  <Button onClick={handleEffectivenessReport}>
                    Сформувати
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          </CardContent>
        </Card>

        {/* VIN Report Card */}
        <Card className="flex w-full flex-col justify-between shadow-sm sm:h-[330px] sm:w-[300px]">
          <CardHeader className="space-y-1 pb-2">
            <CardTitle className="text-xl font-medium leading-6 text-primary">
              {reportAnalytics.vinReport.title}
            </CardTitle>
            <CardDescription className="text-xs font-medium text-primary">
              {reportAnalytics.vinReport.description}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col items-end justify-between sm:h-full">
            <div className="mb-5 space-y-2.5">
              <p className="text-xs font-medium text-primary">
                <span className="mr-1">&#9679;</span>
                {reportAnalytics.vinReport.metrics}
              </p>
            </div>
            <Popover>
              <PopoverTrigger asChild>
                <Button className="w-full" variant="outline">
                  Обрати дати
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className="flex w-auto flex-col gap-2 p-2"
                align="center"
                side={isMobile ? 'bottom' : 'right'}
                sideOffset={10}
                avoidCollisions={true}
                collisionPadding={20}
                style={{ maxHeight: '90vh', overflowY: 'auto' }}
              >
                <Calendar
                  initialFocus
                  mode="range"
                  defaultMonth={date?.from}
                  selected={date}
                  onSelect={setDate}
                  numberOfMonths={getNumberOfMonths()}
                  locale={uk}
                  className="max-w-full"
                  classNames={{
                    months:
                      'space-y-4 sm:space-x-4 sm:space-y-0 flex flex-col sm:flex-row',
                    month: 'space-y-4 w-full',
                  }}
                />
                <div className="sticky bottom-0 flex w-full justify-end bg-background p-2">
                  <Button onClick={handleVinReport}>Сформувати</Button>
                </div>
              </PopoverContent>
            </Popover>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
